import { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight, MapPinned } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { API, SITE, waLink } from "../lib/site";
import { WaIcon } from "../components/icons";
import { KITCHEN_CATEGORIES } from "../data/kitchens";

const FINISHES = ["Gloss", "Matte", "Wood / Textured", "Not sure yet"];
const SIZES = ["Under 60 sq ft", "60 - 100 sq ft", "100 - 150 sq ft", "150+ sq ft"];

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  city: "",
  kitchen_type: "",
  kitchen_size: "",
  preferred_finish: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name.trim().length > 1 && form.phone.replace(/\D/g, "").length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact_form" });
      setSent(true);
      setForm(emptyForm);
      toast.success("Enquiry received. Our team will contact you soon.");
    } catch (err) {
      toast.error("Could not submit right now. Please WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  const waMessage = `Hi Paramount Kitchen! I would like to discuss my kitchen requirement.${
    form.name ? ` — ${form.name}` : ""
  }${form.city ? `, ${form.city}` : ""}`;

  return (
    <>
      <SEO
        title="Contact — Let's Build Your Kitchen | Paramount Kitchen, Kangra"
        description="Request a consultation with Paramount Kitchen. Call, WhatsApp or write to us — premium modular kitchens in Kangra District, Himachal Pradesh."
      />

      <section className="bg-royal pb-20 pt-36 text-ivory md:pb-28 md:pt-44">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow text-gold">Contact</p>
            <h1 className="text-balance mt-5 max-w-3xl font-display text-4xl font-medium leading-tight md:text-6xl">
              Let&rsquo;s build <span className="serif-accent text-gold">your kitchen.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-ivory/65">
              Tell us about your space and how you cook — our team will get back to you with the
              right questions and honest guidance.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-3xl border border-ink/10 bg-white p-8 shadow-sm md:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-ink/60">Name *</span>
                  <input
                    data-testid="contact-input-name"
                    className="premium-input"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Your full name"
                    required
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-ink/60">Phone Number *</span>
                  <input
                    data-testid="contact-input-phone"
                    className="premium-input"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="+91 ·····"
                    inputMode="tel"
                    required
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-ink/60">Email</span>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    className="premium-input"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@example.com"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-ink/60">City</span>
                  <input
                    data-testid="contact-input-city"
                    className="premium-input"
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    placeholder="e.g. Dharamshala"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-ink/60">Kitchen Type</span>
                  <select
                    data-testid="contact-input-kitchen-type"
                    className="premium-input"
                    value={form.kitchen_type}
                    onChange={(e) => set("kitchen_type", e.target.value)}
                  >
                    <option value="">Select kitchen type</option>
                    {KITCHEN_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-ink/60">Approximate Kitchen Size</span>
                  <select
                    data-testid="contact-input-kitchen-size"
                    className="premium-input"
                    value={form.kitchen_size}
                    onChange={(e) => set("kitchen_size", e.target.value)}
                  >
                    <option value="">Select size</option>
                    {SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-ink/60">Preferred Finish</span>
                  <select
                    data-testid="contact-input-finish"
                    className="premium-input"
                    value={form.preferred_finish}
                    onChange={(e) => set("preferred_finish", e.target.value)}
                  >
                    <option value="">Select finish</option>
                    {FINISHES.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-ink/60">Message</span>
                  <textarea
                    data-testid="contact-input-message"
                    rows={4}
                    className="premium-input resize-none"
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder="Tell us about your kitchen, timeline or anything else…"
                  />
                </label>
              </div>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={!valid || sending}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-royal px-8 py-4 text-sm font-semibold text-ivory shadow-md transition-all duration-300 enabled:hover:-translate-y-0.5 enabled:hover:bg-royaldeep disabled:opacity-40"
                >
                  {sent ? "Enquiry Received" : sending ? "Sending…" : "Request a Consultation"}
                  {!sending && <ArrowRight className="h-4 w-4" />}
                </button>
                <a
                  data-testid="contact-whatsapp"
                  href={waLink(waMessage)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#128C4B] px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5"
                >
                  <WaIcon className="h-4 w-4" /> WhatsApp Us
                </a>
              </div>
              {sent && (
                <p data-testid="contact-success" className="mt-5 rounded-xl bg-gold/15 px-5 py-4 text-center text-sm font-medium text-wood">
                  Thank you. Our team will contact you regarding your kitchen requirement.
                </p>
              )}
            </form>
          </Reveal>

          <div className="space-y-6">
            <Reveal delay={0.1}>
              <div className="rounded-3xl bg-royal p-8 text-ivory md:p-10">
                <h2 className="font-display text-2xl font-medium">Talk to us directly.</h2>
                <ul className="mt-8 space-y-5 text-sm">
                  <li>
                    <a data-testid="contact-info-phone" href="tel:+916230492888" className="group flex items-start gap-4">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/10 text-gold">
                        <Phone className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block text-xs uppercase tracking-[0.16em] text-ivory/50">Phone &amp; WhatsApp</span>
                        <span className="mt-1 block font-semibold transition-colors group-hover:text-gold">{SITE.phoneDisplay}</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a data-testid="contact-info-email" href={`mailto:${SITE.email}`} className="group flex items-start gap-4">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/10 text-gold">
                        <Mail className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block text-xs uppercase tracking-[0.16em] text-ivory/50">Email</span>
                        <span className="mt-1 block break-all font-semibold transition-colors group-hover:text-gold">{SITE.email}</span>
                      </span>
                    </a>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/10 text-gold">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.16em] text-ivory/50">Location</span>
                      <span className="mt-1 block font-semibold">{SITE.location}</span>
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div
                data-testid="contact-map-placeholder"
                className="relative flex min-h-[280px] flex-col items-center justify-center overflow-hidden rounded-3xl border border-ink/10 bg-parch p-8 text-center"
              >
                <div className="absolute inset-0 opacity-[0.35] [background-image:radial-gradient(circle_at_center,#0A2540_1px,transparent_1px)] [background-size:22px_22px]" />
                <div className="relative">
                  <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-royal text-gold shadow-lg">
                    <MapPinned className="h-6 w-6" />
                  </span>
                  <p className="mt-5 font-display text-xl font-medium text-ink">Kangra District, Himachal Pradesh</p>
                  <p className="mt-2 text-xs text-ink/50">
                    Google Maps embed placeholder — showroom location to be added.
                  </p>
                  <a
                    data-testid="contact-open-maps"
                    href={SITE.mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-royal px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-ivory transition-transform hover:-translate-y-0.5"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
